import type { AISearchUIMessage } from './tools';
import type { AISearchSearchResult } from './types';

/**
 * A tool part can briefly report `state: 'output-available'` while the SSE
 * stream is still populating `output` — so `output` may be undefined or a
 * partial object mid-stream. Treat the search output as usable only once it
 * has the fields the renderers actually read. Anything less keeps showing the
 * "searching" indicator instead of throwing.
 */
export const isCompleteSearchOutput = (output: unknown): output is AISearchSearchResult => {
  if (!output || typeof output !== 'object') return false;
  const o = output as Partial<AISearchSearchResult>;
  return (
    Array.isArray(o.candidates) &&
    typeof o.totalResults === 'number' &&
    o.fields != null &&
    o.filters != null
  );
};

/**
 * The latest completed `searchSuppliers` output in a message's parts, or null
 * when there's no usable result yet. Shared by the chat renderer and the
 * Provider's `onFinish` (BQ analytics + Redux mirror).
 */
export const findLatestSearchOutput = (
  parts: AISearchUIMessage['parts'],
): AISearchSearchResult | null => {
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    if (
      part?.type === 'tool-searchSuppliers' &&
      part.state === 'output-available' &&
      isCompleteSearchOutput(part.output)
    ) {
      return part.output;
    }
  }
  return null;
};
